import React, { useEffect, useState } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  TextField, MenuItem, Select, InputLabel, FormControl, Container, 
  Grid, Button 
} from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';
import dayjs from 'dayjs'; // For date formatting
import { useNavigate } from 'react-router-dom';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import DailyDealerSummary from './DailyDealerSummary';


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
}));

function Stock() {
  const [stock, setStock] = useState([]);
  const [filteredStock, setFilteredStock] = useState([]); // Show all stock initially
  const [drawDate, setDrawDate] = useState(dayjs().format('YYYY-MM-DD')); // Default to today
  const [searchQuery, setSearchQuery] = useState("");
  const [distinctDrawDates, setDistinctDrawDates] = useState([]);

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const res = await axios.get("https://servermunidasa.ccsoftwares.com/stock/getStock");
        setStock(res.data);
        setFilteredStock(res.data); // Initialize filtered stock with all stock data

        // Extract unique draw dates
        const uniqueDates = [...new Set(res.data.map((row) => new Date(row.drawDate).toISOString().split('T')[0]))];
        setDistinctDrawDates(uniqueDates);

      } catch (error) {
        console.error('Error fetching stock data:', error);
      }
    };
    fetchStockData();
  }, []);

  useEffect(() => {
    filterData();
  }, [drawDate, searchQuery, stock]);

  // Filtering logic
  const filterData = () => {
    const filtered = stock.filter((row) => {
      const rowDrawDate = new Date(row.drawDate).toISOString().split('T')[0];
      const matchesDate = rowDrawDate === drawDate || (drawDate === dayjs().format('YYYY-MM-DD') && rowDrawDate === dayjs().format('YYYY-MM-DD'));
      const matchesSearch = row.lottery.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesDate && matchesSearch;
    });
    setFilteredStock(filtered);
  };

  const handleClearFilters = () => {
    setDrawDate(dayjs().format('YYYY-MM-DD')); // Reset to today
    setSearchQuery(''); // Clear search query
    setFilteredStock(stock); // Reset filtered stock to all stock data
  };

  const navigate = useNavigate();

  const generatePDF = () => {
    const pdf = new jsPDF();
  
    // Set the agency name centered in bold
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("M.M.Munidasa Lottery Agency", pdf.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
  
    // Set the address centered in a smaller font
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    const addressLines = [
      "175/23/E, Mahawatta Road,",
      "Pelanwatta, Pannipitiya",
      "Tel. (+94) 788 393 515"
    ];
  
    addressLines.forEach((line, index) => {
      pdf.text(line, pdf.internal.pageSize.getWidth() / 2, 30 + (index * 5), { align: 'center' });
    });
  
    // Draw a horizontal line below the address
    const lineY = 50; // Y position for the line
    pdf.setLineWidth(0.5); // Line width
    pdf.line(10, lineY, pdf.internal.pageSize.getWidth() - 10, lineY); // Draw the line from left to right
  
    // Set today's date on the left side
    const todayDate = dayjs().format('YYYY-MM-DD');
    pdf.setFontSize(12);
    pdf.text(`Date: ${todayDate}`, 10, 45); // Add today's date
  
    // Create a table for the PDF excluding Creation Date and Last Updated
    const pdfTable = filteredStock.map((row) => [
      row.LB,
      row.lottery,
      new Date(row.drawDate).toLocaleDateString("en-US", { timeZone: "Asia/Colombo" }),
      row.currentStock,
      row.remainingStock,
    ]);
  
    // Add a gap before the table
    pdf.setFontSize(12); // Set font size for table
    pdf.autoTable({
      head: [['Lottery Board', 'Lottery', 'Draw Date', 'Initial Stock', 'Remaining Stock']],
      body: pdfTable,
      startY: 55, // Start the table after the address and date
      styles: { 
        overflow: 'linebreak',
        cellPadding: 3,
        fontSize: 10,
        // Setting header styles
        fillColor: [255, 255, 255], // White background
        textColor: [0, 0, 0], // Black text
      },
      headStyles: {
        fillColor: [255, 255, 255], // White background for header
        textColor: [0, 0, 0], // Black text for header
      },
      theme: 'grid', // You can customize the table theme here
    });
  
    pdf.setFontSize(10); // Set font size for footer
    pdf.text("CC Softwares @ 2024", pdf.internal.pageSize.getWidth() / 2, pdf.autoTable.previous.finalY + 10, { align: 'center' });
  
    // Save the PDF
    pdf.save('stock_report.pdf');
  };
  

  return (
    <Container maxWidth="lg">

      <Grid>
        <DailyDealerSummary/>
      </Grid>

      <Grid xs={12} md={12}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'left', height: 40 ,marginTop : 20}}>
          <button
            style={{
              backgroundColor: '#E7EDFA',
              color: 'Black',
              border: 'none',
              cursor: 'pointer',
              fontSize: 30,
              marginTop: 10
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowCircleLeftIcon fontSize='2' />
          </button>
          <div style={{ fontFamily: 'Segoe UI', fontSize: 28, fontWeight: '400' }}>
            Ticket Stock
          </div>
        </div>
      </Grid>
      <Paper elevation={3} style={{ padding: '20px', marginTop: '20px' }}>
        <Grid container spacing={2}> {/* Adjusted spacing here */}
          <Grid item md={6} xs={12}>
            <FormControl fullWidth sx={{ marginBottom: '20px' }}>
              <InputLabel>Draw Date</InputLabel>
              <Select
                value={drawDate}
                label="Draw Date"
                onChange={(e) => setDrawDate(e.target.value)}
              >
                <MenuItem value={dayjs().format('YYYY-MM-DD')}>Today ({dayjs().format('YYYY-MM-DD')})</MenuItem>
                {distinctDrawDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {new Date(date).toLocaleDateString("en-US", { timeZone: "Asia/Colombo" })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              label="Search Lottery"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ marginBottom: '20px' }}
            />
          </Grid>
          <Grid item md={12} xs={12} style={{ display: 'flex', justifyContent: 'space-between',marginBottom : 10 }}>
            <Button variant="outlined" onClick={handleClearFilters} style={{ marginRight: '20px' }}>
              Clear Filters
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={generatePDF}
            >
              Download PDF
            </Button>
          </Grid>
        </Grid>

        <TableContainer component={Paper} id="table-to-pdf">
          <Table sx={{ minWidth: 650 }} aria-label="lottery stock table">
            <TableHead>
              <TableRow>
                <TableCell align="center">Lottery Board</TableCell>
                <TableCell align="center">Lottery</TableCell>
                <TableCell align="center">Draw Date</TableCell>
                <TableCell align="center">Initial Stock</TableCell>
                <TableCell align="center">Remaining Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStock.map((row) => (
                <StyledTableRow key={row.lottery}>
                  <TableCell align="center">{row.LB}</TableCell>
                  <TableCell align="center">{row.lottery}</TableCell>
                  <TableCell align="center">{new Date(row.drawDate).toLocaleDateString("en-US", { timeZone: "Asia/Colombo" })}</TableCell>
                  <TableCell align="center">{row.currentStock}</TableCell>
                  <TableCell align="center">{row.remainingStock}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
    </Container>
    
  );
}

export default Stock;
